import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Search, DollarSign, Eye } from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentPayments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      issued: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const filteredPayments = payments.filter(payment => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      payment.payment_id?.toLowerCase().includes(searchLower) ||
      payment.claim_id?.toLowerCase().includes(searchLower) ||
      payment.check_number?.toLowerCase().includes(searchLower) ||
      payment.user?.email?.toLowerCase().includes(searchLower) ||
      payment.user?.full_name?.toLowerCase().includes(searchLower)
    );
  });

  const totalIssued = payments.reduce((sum, p) => p.status === "issued" ? sum + p.amount : sum, 0);

  return (
    <AgentLayout title="Payments">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          Payment Tracking
        </h1>
        <p className="text-muted-foreground">Track and manage claim payments</p>
      </div>

      {/* Summary Card */}
      <Card className="card-organic mb-6">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Payments Issued</p>
              <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                ${totalIssued.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-primary/10 p-4 rounded-full">
              <DollarSign className="h-8 w-8 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search payments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="payment-search-input"
          />
        </div>
      </div>

      {/* Payments Table */}
      <Card className="card-organic overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredPayments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Payment ID</th>
                    <th>Claim</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Check #</th>
                    <th>Status</th>
                    <th>Issued</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => (
                    <tr key={payment.payment_id}>
                      <td className="font-mono text-sm">{payment.payment_id}</td>
                      <td className="font-mono text-sm">{payment.claim_id}</td>
                      <td>
                        <div>
                          <p className="font-medium">{payment.user?.full_name || "N/A"}</p>
                          <p className="text-sm text-muted-foreground">{payment.user?.email}</p>
                        </div>
                      </td>
                      <td className="font-semibold text-green-600">
                        ${payment.amount.toFixed(2)}
                      </td>
                      <td className="font-mono">{payment.check_number || "N/A"}</td>
                      <td>
                        <Badge className={getStatusBadge(payment.status)}>
                          {payment.status}
                        </Badge>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {new Date(payment.issued_at).toLocaleDateString()}
                      </td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/agent/claims/${payment.claim_id}`)}
                          data-testid={`view-payment-claim-${payment.payment_id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Claim
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-muted rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">No payments issued yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </AgentLayout>
  );
}
